import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGO_URL } from "@/lib/constants";
import { AlertCircle, Mail } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const em = email.trim().toLowerCase();
    if (!em || !EMAIL_RE.test(em)) return setErr("Please enter a valid email address.");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: em });
      setSent(true);
    } catch (e) {
      // Shouldn't happen (endpoint always 200), but just in case
      setErr(e?.response?.data?.detail || `Request failed (HTTP ${e?.response?.status || "unknown"}). Please try again.`);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md bg-white rounded-md border border-slate-200 p-8">
        <Link to="/" className="flex items-center gap-3 mb-6">
          <img src={LOGO_URL} alt="logo" className="h-10 w-10 object-contain" />
          <div className="font-heading font-black text-slate-900">CLEAVIE'S ENTERPRISE</div>
        </Link>

        {sent ? (
          <div data-testid="forgot-sent">
            <div className="flex items-center gap-2 text-emerald-700 mb-2">
              <Mail className="h-5 w-5" />
              <div className="tag-overline text-emerald-700">Check your inbox</div>
            </div>
            <h1 className="font-heading font-black text-2xl text-slate-900">Reset link sent</h1>
            <p className="mt-3 text-sm text-slate-600">
              If an account exists for <strong>{email}</strong>, we've sent a password reset link.
              The link expires in 1 hour.
            </p>
            <p className="mt-3 text-xs text-slate-500">
              Didn't get the email? Check spam, or call Cleavie's directly — an admin can reset
              your password for you right over the phone.
            </p>
            <Link to="/login" className="mt-6 inline-block font-bold text-[#1D4ED8] hover:underline"
                  data-testid="forgot-back-to-login">
              ← Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="space-y-5">
            <div>
              <div className="tag-overline">Forgot password</div>
              <h1 className="font-heading font-black text-2xl text-slate-900 mt-1">Reset your password</h1>
              <p className="text-sm text-slate-600 mt-2">
                Enter the email you signed up with. We'll send you a link to choose a new password.
              </p>
            </div>

            {err && (
              <div data-testid="forgot-error" className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-800 rounded-md p-3 text-sm">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>{err}</div>
              </div>
            )}

            <div>
              <Label className="text-sm font-bold text-slate-900">Email</Label>
              <Input type="email" inputMode="email" autoComplete="email" data-testid="forgot-email"
                     value={email} onChange={e => setEmail(e.target.value)} />
            </div>

            <Button type="submit" disabled={busy} data-testid="forgot-submit-btn"
                    className="w-full h-11 bg-[#E32636] hover:bg-[#C91F2C] text-white font-bold">
              {busy ? "Sending…" : "Send reset link"}
            </Button>

            <p className="text-sm text-slate-600 text-center">
              Remembered it? <Link to="/login" className="font-bold text-[#1D4ED8] hover:underline">Sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
